import {useEffect} from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import type {Control, FieldValues} from "react-hook-form";
import {Input, Select, Space} from "antd";
import {FormField, FormFieldError} from "../../../design-system/form-field";
import {COUNTRY_CODES_WITH_FLAG} from "../../../services/country-code";

export interface PhoneNumberInputProps {
  /**
   * RHF `control` from `useForm()` / `useFormContext()`. The component also
   * expects to be rendered inside a `FormProvider` — it reads `trigger` and
   * `formState` from context to re-validate on country change.
   */
  control: Control<FieldValues>;
  /** Field name holding the national number digits. Default: `"phoneNumber"` */
  name?: string;
  /** Field name holding the dial code (e.g. `"+234"`). Default: `"numberCode"` */
  numberCodeName?: string;
  /** DOM id for the national-number input (used by the label). Default matches `name`. */
  id?: string;
  /** Field label rendered above the input. Default: `"Phone number"` */
  label?: string;
  /** Placeholder for the national-number input. Default: `"Enter phone number"` */
  placeholder?: string;
  /** Disables both the dial-code select and the national-number input. */
  disabled?: boolean;
}

export function PhoneNumberInput({
  control,
  name = "phoneNumber",
  numberCodeName = "numberCode",
  id,
  label = "Phone number",
  placeholder = "Enter phone number",
  disabled,
}: PhoneNumberInputProps) {
  const {formState, trigger} = useFormContext();
  const phoneNumberError = (formState.errors?.[name] as {message?: string} | undefined)
    ?.message;
  const numberCodeError = (
    formState.errors?.[numberCodeName] as {message?: string} | undefined
  )?.message;

  const numberCode = useWatch({control, name: numberCodeName});
  const isDirty = Boolean((formState.dirtyFields as Record<string, unknown>)[name]);
  const isTouched = Boolean((formState.touchedFields as Record<string, unknown>)[name]);
  useEffect(() => {
    if (isDirty || isTouched) {
      trigger(name);
    }
  }, [numberCode, trigger, name, isDirty, isTouched]);

  const resolvedId = id ?? name;

  return (
    <FormField
      id={resolvedId}
      label={label}
      renderInput={(inputId) => (
        <Controller
          control={control}
          name={name}
          render={({field: {onChange, value, ref, onBlur}}) => (
            <Space size={8} direction="vertical" style={{display: "flex"}}>
              <Input
                id={inputId}
                status={phoneNumberError || numberCodeError ? "error" : ""}
                onChange={onChange}
                onBlur={onBlur}
                size="large"
                value={value === null ? undefined : value}
                placeholder={placeholder}
                ref={ref}
                disabled={disabled}
                addonBefore={
                  <Controller
                    control={control}
                    name={numberCodeName}
                    render={({field}) => (
                      <Select
                        {...field}
                        size="large"
                        showSearch
                        disabled={disabled}
                        filterOption={(input, option) => {
                          const search = input.toLowerCase();
                          return [option?.countryName, option?.value].some((text) =>
                            String(text ?? "")
                              .toLowerCase()
                              .includes(search)
                          );
                        }}
                        style={{width: 120}}
                      >
                        {COUNTRY_CODES_WITH_FLAG.map((code) => (
                          <Select.Option
                            key={code.isoCode}
                            value={code.dialCode}
                            countryName={code.name}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1rem",
                              }}
                            >
                              <img
                                src={code.flag}
                                width={30}
                                height={20}
                                alt={code.name}
                              />
                              <span>{code.dialCode}</span>
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  />
                }
              />
              <FormFieldError errorMessage={phoneNumberError || numberCodeError} />
            </Space>
          )}
        />
      )}
    />
  );
}
